import { memo } from 'react'
import { useI18n } from '../i18n/I18nContext'

interface SearchFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  language: string
  onLanguageChange: (value: string) => void
  topic: string
  onTopicChange: (value: string) => void
  sort: 'stars' | 'updated'
  onSortChange: (value: 'stars' | 'updated') => void
  perPage: number
  onPerPageChange: (value: number) => void
  includePrivate: boolean
  onIncludePrivateChange: (value: boolean) => void
  languages: string[]
  topics: string[]
  onResetFilters: () => void
}

export const SearchFilters = memo(function SearchFilters({
  search,
  onSearchChange,
  language,
  onLanguageChange,
  topic,
  onTopicChange,
  sort,
  onSortChange,
  perPage,
  onPerPageChange,
  includePrivate,
  onIncludePrivateChange,
  languages,
  topics,
  onResetFilters,
}: SearchFiltersProps) {
  const { t } = useI18n()
  const hasActiveFilters = search || language || topic || includePrivate

  return (
    <div className="filters-container">
      <div className="filters-row">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder={t('forms.search.placeholder')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="search-input"
            aria-label={t('forms.search.ariaLabel')}
          />
          {search && (
            <button
              className="clear-search"
              onClick={() => onSearchChange('')}
              aria-label={t('forms.clearSearch.ariaLabel')}
            >
              ✕
            </button>
          )}
        </div>

        <select
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          className="filter-select"
          aria-label={t('forms.language.ariaLabel')}
        >
          <option value="">{t('forms.language.all')}</option>
          {languages.map(l => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>

        <select
          value={topic}
          onChange={(e) => onTopicChange(e.target.value)}
          className="filter-select"
          aria-label={t('forms.topic.ariaLabel')}
        >
          <option value="">{t('forms.topic.all')}</option>
          {topics.map(tp => (
            <option key={tp} value={tp}>#{tp}</option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as 'stars' | 'updated')}
          className="filter-select"
          aria-label={t('forms.sort.ariaLabel')}
        >
          <option value="stars">{t('forms.sort.stars')}</option>
          <option value="updated">{t('forms.sort.updated')}</option>
        </select>

        <select
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          className="filter-select"
          aria-label={t('forms.perPage.ariaLabel')}
        >
          {[12, 24, 36, 60].map(n => (
            <option key={n} value={n}>{t('forms.perPage.option', { n })}</option>
          ))}
        </select>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includePrivate}
            onChange={(e) => onIncludePrivateChange(e.target.checked)}
            aria-label={t('forms.private.ariaLabel')}
          />
          <span>{t('forms.private.label')}</span>
        </label>

        {hasActiveFilters && (
          <button
            className="btn-reset-filters"
            onClick={onResetFilters}
            title={t('forms.reset.title')}
          >
            {t('forms.reset')}
          </button>
        )}
      </div>
    </div>
  )
})
